import {
  type AdminGameRes,
  GameStatuses,
  type RenGameReq,
  v2GamesAdminDetail,
  v2GamesRenovateUpdate,
} from '@on3/api';
import { getGameStatuses } from '@on3/api/utils/games';
import { Checkbox } from '@on3/ui-lib/components/Checkbox/Checkbox';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { Check, XMark } from '@on3/ui-lib/components/Svg';
import { useClickOutside } from '@on3/ui-lib/hooks/useClickOutside';
import { externalApi } from '@on3/ui-lib/index';
import { getErrors, yup } from '@on3/ui-lib/utils/yup';
import { AdminLink } from '@public/components/User/AdminLink';
import { GameWrapperContext } from '@public/hocs/Game/GameWrapper';
import {
  type FormEvent,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';

import styles from './Edit.module.scss';

interface GameState {
  fetching: boolean;
  processing: boolean;
  values: AdminGameRes | null;
  errors: Record<string, string> | null;
  saved: boolean;
}

type ActionType =
  | { type: 'fetch' }
  | { type: 'init'; payload: AdminGameRes }
  | { type: 'update'; field: string; value: any }
  | { type: 'submit' }
  | { type: 'success' }
  | { type: 'error'; payload: Record<string, string> | null };

// Schema for validation
const timeValidation = yup
  .string()
  .nullable()
  .transform((value) =>
    value ? (value.split(':').length === 3 ? value : `${value}:00`) : null,
  )
  .when('startTimeTbd', {
    is: false,
    then: (schema) => schema.required('Start time is required if not TBD.'),
  });

const scoreValidation = yup
  .number()
  .transform((value) => (!value && value !== 0 ? null : Number(value)))
  .nullable();

const schema = yup.object().shape({
  startTimeTbd: yup.boolean(),
  startTimeCentral: timeValidation,
  awayScore: scoreValidation,
  homeScore: scoreValidation,
  status: yup
    .string()
    .when('statusEnum', (statusEnum, s) =>
      s.transform(() => statusEnum.replaceAll(/\s/g, '').toUpperCase()),
    ), // set status to statusEnum transformed value
  statusEnum: yup.string().oneOf(Object.values(GameStatuses)),
  imported: yup.object().strip(),
  overrides: yup.object().strip(),
});

const reducer = (state: GameState, action: ActionType): GameState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, fetching: true };
    case 'init':
      return { ...state, fetching: false, values: action.payload };
    case 'update':
      return {
        ...state,
        values: {
          ...(state.values as AdminGameRes),
          [action.field]: action.value,
        },
        saved: false,
      };
    case 'submit':
      return { ...state, processing: true };
    case 'success':
      return { ...state, processing: false, saved: true };
    case 'error':
      return { ...state, processing: false, errors: action.payload };
    default:
      return state;
  }
};

const initialState: GameState = {
  fetching: true,
  processing: false,
  values: null,
  errors: null,
  saved: false,
};

export const Edit = () => {
  const ref = useRef<HTMLFormElement | null>(null);
  const { game, close } = useContext(GameWrapperContext);

  const editTeamSlug =
    game.homeTeamOrganization?.urlSlug || game.awayTeamOrganization?.urlSlug;

  useClickOutside(ref, () => close());

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    v2GamesAdminDetail(externalApi, game.key)
      .then((response) => {
        dispatch({ type: 'init', payload: response });
      })
      .catch((e) => {
        dispatch({
          type: 'error',
          payload: { message: e.message },
        });
      });
  }, [game.key]);

  const handleSave = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      dispatch({ type: 'error', payload: null });
      dispatch({ type: 'submit' });

      // workaround for ts so as not to create mapping helper function
      let gameData = state.values as AdminGameRes | RenGameReq;

      try {
        gameData = (await schema.validate(gameData, {
          abortEarly: false,
        })) as unknown as RenGameReq;
      } catch (error: any) {
        dispatch({ type: 'error', payload: getErrors(error) });

        return;
      }

      await v2GamesRenovateUpdate(externalApi, gameData)
        .then(() => {
          dispatch({ type: 'success' });
        })
        .catch((_) => {
          dispatch({
            type: 'error',
            payload: { message: 'There was an error saving the game.' },
          });
        });
    },
    [state],
  );

  return (
    <form className={styles.popout} onSubmit={handleSave} ref={ref}>
      {!state.values || state.fetching ? (
        <p>Loading...</p>
      ) : (
        <>
          <button name="close" onClick={close} type="button">
            <XMark />
          </button>
          <p className={styles.title}>
            {game.awayTeamOrganization?.name} at{' '}
            {game.homeTeamOrganization?.name}
          </p>

          <AdminLink
            compact
            path={`/organization/${editTeamSlug}/schedule/game/${game.key}/edit/`}
            title="Full Game Edit"
          />

          <p>Times are saved in central time.</p>
          <div className={styles.section}>
            <Input
              label="Start date"
              onChange={(e) =>
                dispatch({
                  type: 'update',
                  field: 'startDate',
                  value: e.target.value,
                })
              }
              type="date"
              value={state.values?.startDate ?? ''}
            />
            <Input
              disabled={state.values?.startTimeTbd}
              error={state.errors?.startTimeCentral}
              label="Start time"
              onChange={(e) =>
                dispatch({
                  type: 'update',
                  field: 'startTimeCentral',
                  value: e.target.value,
                })
              }
              type="time"
              value={state.values?.startTimeCentral ?? ''}
            />
          </div>
          <Checkbox
            id="startTimeTbd"
            label="Game Time TBD"
            onChange={(checked) =>
              dispatch({
                type: 'update',
                field: 'startTimeTbd',
                value: checked ? true : false,
              })
            }
            value={state.values?.startTimeTbd}
          />
          <div className={styles.section}>
            <Input
              error={state.errors?.awayScore}
              label={`${game.awayTeamOrganization?.name} Score`}
              onChange={(e) =>
                dispatch({
                  type: 'update',
                  field: 'awayScore',
                  value: e.target.value,
                })
              }
              type="number"
              value={String(state.values?.awayScore ?? '')}
            />
            <Input
              error={state.errors?.homeScore}
              label={`${game.homeTeamOrganization?.name} Score`}
              onChange={(e) =>
                dispatch({
                  type: 'update',
                  field: 'homeScore',
                  value: e.target.value,
                })
              }
              type="number"
              value={String(state.values?.homeScore ?? '')}
            />
          </div>
          <Select
            label="Status"
            onChange={(e) =>
              dispatch({
                type: 'update',
                field: 'statusEnum',
                value: e.target.value,
              })
            }
            options={getGameStatuses()}
            value={state.values?.statusEnum}
          />
          <button
            data-saved={state.saved}
            disabled={state.processing}
            style={{ background: game.currentTeam.primaryColor }}
            type="submit"
          >
            {state.saved ? (
              <>
                <span>Saved</span>
                <Check />
              </>
            ) : (
              'Save'
            )}
          </button>
        </>
      )}
      {state.errors?.message && (
        <p className={styles.error}>{state.errors.message}</p>
      )}
    </form>
  );
};
